/**
 * App.js
 * ------
 * Public website for Maa Briquette.
 * Public routes: / and /waste.
 */

import { useEffect, useState } from 'react';
import {
  C,
  CSS,
  PRODUCTS,
  FORMSPREE_ORDER_ENDPOINT,
  FORMSPREE_WASTE_ENDPOINT,
  FORMSPREE_CONTACT_ENDPOINT,
} from './constants';

const PHONE_DISPLAY = '+254 728 284 768';
const PHONE_TEL = 'tel:+254728284768';
const WHATSAPP_URL = 'https://wa.me/254728284768';

async function submitToFormspree(endpoint, payload) {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // Formspree can return an empty response body.
  }

  if (!res.ok) {
    throw new Error(
      (data && (data.error || data.message)) ||
        'Something went wrong. Please try again.'
    );
  }

  return data;
}

function scrollToHash(hash) {
  if (!hash) return;
  window.requestAnimationFrame(() => {
    document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
  });
}

function navigate(path, hash = '') {
  const next = `${path}${hash}`;
  if (`${window.location.pathname}${window.location.hash}` !== next) {
    window.history.pushState({}, '', next);
    window.dispatchEvent(new Event('popstate'));
  }
  window.setTimeout(() => {
    if (hash) {
      scrollToHash(hash);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, 50);
}

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: C.mist }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: C.smoke, marginTop: 5 }}>{hint}</div>}
  </div>
);

const SectionHeading = ({ kicker, title, body, color = C.ember }) => (
  <div style={{ textAlign: 'center', marginBottom: 44 }}>
    <span className="mono" style={{ fontSize: 11, color, letterSpacing: 2, textTransform: 'uppercase' }}>
      {kicker}
    </span>
    <h2 className="pf" style={{ fontSize: 34, marginTop: 8, marginBottom: 10 }}>
      {title}
    </h2>
    {body && <p style={{ color: C.smoke, maxWidth: 560, margin: '0 auto', lineHeight: 1.7 }}>{body}</p>}
  </div>
);

const Navbar = ({ path }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = path === '/waste'
    ? [
        ['Home', '/', ''],
        ['About', '/', '#about'],
        ['Products', '/', '#products'],
        ['Order', '/', '#order'],
        ['Contact', '/', '#contact'],
      ]
    : [
        ['Home', '/', '#home'],
        ['About', '/', '#about'],
        ['Products', '/', '#products'],
        ['Benefits', '/', '#benefits'],
        ['Order', '/', '#order'],
        ['Contact', '/', '#contact'],
      ];
  const mobileLinks = [
    ['Home', '/', '#home'],
    ['About', '/', '#about'],
    ['Products', '/', '#products'],
    ['Benefits', '/', '#benefits'],
    ['Order', '/', '#order'],
    ['Contact', '/', '#contact'],
    ['Waste Collection', '/waste', ''],
  ];
  const handleNav = (targetPath, hash = '') => {
    navigate(targetPath, hash);
    setIsMenuOpen(false);
  };

  return (
    <nav className="site-nav" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: scrolled ? 'rgba(13,13,13,.97)' : 'transparent', backdropFilter: scrolled ? 'blur(20px)' : 'none', borderBottom: scrolled ? `1px solid ${C.ash}` : '1px solid transparent', transition: 'all .3s', padding: '0 4%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 68, gap: 12 }}>
      <a className="nav-brand" href="/#home" onClick={(e) => { e.preventDefault(); navigate('/', '#home'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: C.cream, flexShrink: 0 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
          <img src="/maa logo.png" alt="Maa Briquette" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
        </div>
        <span className="pf" style={{ fontSize: 19, fontWeight: 700 }}>Maa Briquette</span>
      </a>

      <div className="nav-links" style={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {links.map(([label, targetPath, hash]) => (
          <a
            key={`${targetPath}${hash || label}`}
            className="btn-g"
            href={`${targetPath}${hash}`}
            onClick={(e) => { e.preventDefault(); handleNav(targetPath, hash); }}
            style={{ textDecoration: 'none', color: path === '/' ? C.mist : C.smoke }}
          >
            {label}
          </a>
        ))}
        <a
          className="btn-g"
          href="/waste"
          onClick={(e) => { e.preventDefault(); handleNav('/waste'); }}
          style={{ textDecoration: 'none', color: path === '/waste' ? C.leafL : C.mist, fontWeight: path === '/waste' ? 700 : 400, background: path === '/waste' ? 'rgba(74,124,89,.12)' : 'transparent' }}
        >
          Waste Collection
        </a>
      </div>
      <button
        className={`hamburger ${isMenuOpen ? 'open' : ''}`}
        type="button"
        aria-label="Toggle navigation menu"
        aria-expanded={isMenuOpen}
        onClick={() => setIsMenuOpen((open) => !open)}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 8 }}
      >
        <span />
        <span />
        <span />
      </button>
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        {mobileLinks.map(([label, targetPath, hash]) => (
          <a
            key={`mobile-${targetPath}${hash || label}`}
            href={`${targetPath}${hash}`}
            onClick={(e) => { e.preventDefault(); handleNav(targetPath, hash); }}
            style={{ color: label === 'Waste Collection' ? C.leafL : C.cream, textDecoration: 'none' }}
          >
            {label}
          </a>
        ))}
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section id="home" style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', backgroundImage: `linear-gradient(180deg, rgba(13,13,13,.48) 0%, rgba(13,13,13,.62) 55%, rgba(13,13,13,.86) 100%), radial-gradient(ellipse at 60% 50%, rgba(232,93,4,.15) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(74,124,89,.10) 0%, transparent 50%), url(/homepage-background.jpeg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: C.coal, padding: '160px 5% 70px', gap: 24, scrollMarginTop: 68 }}>
    <div style={{ maxWidth: 610, flex: '1 1 100%', minWidth: 0 }}>
      <div className="fu" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22 }}>
        <div style={{ height: 2, width: 36, background: C.ember }} />
        <span className="mono" style={{ fontSize: 11, color: C.ember, letterSpacing: 2, textTransform: 'uppercase' }}>Maa Briquette - Eco-Friendly Fuel</span>
      </div>
      <h1 className="pf fu2" style={{ fontSize: 'clamp(38px,5.5vw,68px)', lineHeight: 1.1, marginBottom: 22 }}>
        Cleaner Briquette Fuel For <span style={{ color: C.ember }}>Home</span>, <span style={{ color: C.ember }}>Hotel</span> and <span style={{ color: C.ember }}>Business</span> Use.
      </h1>
      <p className="fu3" style={{ fontSize: 17, color: C.mist, lineHeight: 1.75, marginBottom: 32, maxWidth: 500 }}>
        Maa Briquette produces reliable eco-charcoal briquettes from useful waste streams. Our fuel is built for cooking, heating, hospitality, institutions and bulk buyers that need cleaner energy at practical cost.
      </p>
      <div className="fu4" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn-p" style={{ fontSize: 15, padding: '13px 30px' }} onClick={() => navigate('/', '#order')}>Order Now</button>
        <button className="btn-green" style={{ fontSize: 15, padding: '13px 24px' }} onClick={() => navigate('/waste')}>Waste Collection</button>
        <a className="btn-s" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: 15, padding: '13px 24px' }}>WhatsApp Us</a>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 44, paddingTop: 28, borderTop: `1px solid ${C.ash}`, flexWrap: 'wrap' }}>
        {[
          ['Cleaner', 'Low-Smoke Fuel'],
          ['Bulk', 'Hotel Supply'],
          ['Circular', 'Waste-to-Energy'],
        ].map(([n, l]) => (
          <div key={l}>
            <div className="pf" style={{ fontSize: 26, fontWeight: 700, color: C.ember }}>{n}</div>
            <div style={{ fontSize: 12, color: C.smoke, marginTop: 3 }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FirePerformanceSection = () => (
  <section style={{ padding: '88px 5% 92px', background: C.coal }}>
    <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', borderRadius: 10, overflow: 'hidden' }}>
      <img className="fire-image" src="/banner3.jpg" alt="Briquette fire performance" style={{ width: '100%', height: 320, objectFit: 'cover', display: 'block' }} />
      <div className="fire-overlay" style={{ position: 'absolute', left: 22, bottom: 22, maxWidth: 390, padding: 18, borderRadius: 8, background: 'rgba(13,13,13,.72)', border: '1px solid rgba(255,255,255,.12)', backdropFilter: 'blur(8px)', boxShadow: '0 12px 30px rgba(0,0,0,.28)' }}>
        <h2 className="pf" style={{ fontSize: 'clamp(24px,3vw,32px)', lineHeight: 1.12, marginBottom: 8 }}>Real Fire. Real Performance.</h2>
        <p style={{ color: C.mist, lineHeight: 1.55, fontSize: 14, marginBottom: 14 }}>Clean briquette fuel built for homes, hotels, and businesses that need reliable heat.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn-p" onClick={() => navigate('/', '#order')}>Order Briquettes</button>
          <button className="btn-s" onClick={() => navigate('/', '#products')}>View Products</button>
        </div>
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" style={{ padding: '76px 5%', background: C.coal, scrollMarginTop: 68 }}>
    <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 30, alignItems: 'center' }}>
      <div style={{ maxWidth: 600 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ height: 2, width: 32, background: C.ember }} />
          <span className="mono" style={{ fontSize: 11, color: C.ember, letterSpacing: 2 }}>ABOUT MAA BRIQUETTE</span>
        </div>
        <h2 className="pf" style={{ fontSize: 42, lineHeight: 1.15, marginBottom: 14, maxWidth: 520 }}>
          Building a greener Kenya, one briquette at a time.
        </h2>
        <p style={{ color: C.mist, lineHeight: 1.75, marginBottom: 22, maxWidth: 560 }}>
          Maa Briquette is a Narok-based clean fuel business founded from a practical vision: turn underused waste into dependable briquettes that reduce pressure on forests and make everyday cooking and heating more sustainable.
        </p>
        <div style={{ display: 'grid', gap: 14, marginBottom: 22, maxWidth: 560 }}>
          {[
            ['Who we serve', 'Hotels, homes, restaurants, businesses, schools, institutions, and other bulk users that need reliable heat.'],
            ['What we make', 'Eco-friendly briquettes that support cleaner cooking and heating while reducing dependence on traditional charcoal.'],
            ['How it works', 'Briquette production, waste recycling, and hotel or business partnerships come together in a direct waste-to-energy model.'],
          ].map(([title, body]) => (
            <div key={title} className="about-highlight" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14, padding: '14px 0', borderTop: `1px solid ${C.ash}` }}>
              <div style={{ color: C.ember, fontWeight: 700, fontSize: 13 }}>{title}</div>
              <p style={{ color: C.mist, lineHeight: 1.65, fontSize: 14 }}>{body}</p>
            </div>
          ))}
        </div>
        <p style={{ color: C.mist, lineHeight: 1.75, maxWidth: 560, borderLeft: `2px solid ${C.ember}`, paddingLeft: 16 }}>
          The original Maa Briquette story is rooted in George Mochu Karaya's return to Narok after studying Economics at Kenyatta University. What began as a local response to environmental degradation and fuel challenges has grown into a social enterprise focused on cleaner energy, practical recycling, and reliable briquette supply.
        </p>
      </div>
      <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <img src="/George.jpg" alt="Maa Briquette founder" style={{ width: 430, maxWidth: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
      </div>
    </div>
  </section>
);

const ProductsSection = () => (
  <section id="products" style={{ padding: '76px 5%', background: C.charcoal, scrollMarginTop: 68 }}>
    <SectionHeading kicker="Products" title="Briquettes For Every Scale" body="Choose from household, hotel, institutional, industrial, and bulk supply options for cooking, heating, and reliable daily fuel use." />
    <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 22, maxWidth: 1060, margin: '0 auto' }}>
      {PRODUCTS.map((p) => (
        <div key={p.name} className="card card-ember" style={{ padding: 26, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {!p.available && <span className="badge b-cancelled" style={{ position: 'absolute', top: 14, right: 14 }}>Unavailable</span>}
          <div style={{ fontSize: 52, marginBottom: 18, textAlign: 'center', padding: 18, background: 'rgba(232,93,4,.04)', borderRadius: 8 }}>{p.icon}</div>
          <h3 className="pf" style={{ fontSize: 21, marginBottom: 7 }}>{p.name}</h3>
          <div style={{ fontSize: 12, color: C.mist, lineHeight: 1.5, marginBottom: 12 }}>
            <span style={{ color: C.cream, fontWeight: 700 }}>Best for: </span>{p.bestFor.join(', ')}
          </div>
          <p style={{ color: C.smoke, fontSize: 13, lineHeight: 1.6, marginBottom: 14, flex: 1 }}>{p.desc}</p>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
            {p.points.map((item) => (
              <div key={item} style={{ fontSize: 12, color: C.mist, display: 'flex', gap: 8 }}>
                <span style={{ color: C.ember, flexShrink: 0 }}>-</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, paddingTop: 14, borderTop: `1px solid ${C.ash}`, gap: 8, flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: 12, color: C.smoke }}>Price</div><div style={{ fontWeight: 700, color: C.ember }}>{p.price}</div></div>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 12, color: C.smoke }}>Sizes</div><div style={{ fontSize: 13, color: C.mist }}>{p.sizes}</div></div>
          </div>
          <button className={p.available ? 'btn-p' : 'btn-s'} style={{ width: '100%', marginTop: 'auto' }} onClick={() => p.available && navigate('/', '#order')} disabled={!p.available}>
            {p.name === 'Bulk Supply' ? 'Request Bulk Quote' : 'Order This Product'}
          </button>
        </div>
      ))}
    </div>
    <p style={{ color: C.mist, fontSize: 12, lineHeight: 1.6, maxWidth: 760, margin: '18px auto 0', textAlign: 'center' }}>
      Prices shown are FOB (Factory Price) — transport and delivery costs are charged separately depending on location.
    </p>
  </section>
);

const BenefitsSection = () => (
  <section id="benefits" style={{ padding: '76px 5%', background: C.coal, scrollMarginTop: 68 }}>
    <SectionHeading kicker="Why Choose Us" title="Cleaner Fuel With Stronger Performance" body="A practical fuel choice for customers who need consistent heat, predictable supply, cost control, and a stronger environmental story." />
    <div className="benefits-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 18, maxWidth: 1120, margin: '0 auto' }}>
      {[
        ['Cost-Saving Fuel', 'Spend less on daily cooking and heating with dense briquettes that burn longer and reduce repeated fuel purchases.'],
        ['Eco-Friendly Choice', 'Made through a waste-to-energy approach, helping reduce organic waste while offering a cleaner alternative to traditional charcoal.'],
        ['Reliable Supply', 'Built for repeat orders, hotels, restaurants, schools, and institutions that need steady fuel without supply stress.'],
        ['Built For Bulk Buyers', 'Flexible supply for homes, hotels, distributors, and large buyers who need predictable stock and planned deliveries.'],
      ].map(([title, desc]) => (
        <div key={title} className="card card-ember" style={{ padding: 24, height: '100%' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: C.cream }}>{title}</h3>
          <p style={{ fontSize: 13, color: C.smoke, lineHeight: 1.7 }}>{desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const requiredOrderFields = [
  'customerName',
  'phone',
  'email',
  'location',
  'customerType',
  'productSize',
  'quantity',
  'deliveryOption',
];

const OrderSection = () => {
  const [done, setDone] = useState(false);
  const [oid] = useState('ORD-' + Math.random().toString(36).substr(2, 6).toUpperCase());
  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    location: '',
    customerType: '',
    productSize: '',
    quantity: '',
    deliveryOption: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const upd = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const canSubmit = requiredOrderFields.every((key) => form[key]) && !isSubmitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!requiredOrderFields.every((key) => form[key])) return;

    try {
      setIsSubmitting(true);
      await submitToFormspree(FORMSPREE_ORDER_ENDPOINT, {
        ...form,
        oid,
        formPurpose: 'Briquette order request',
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="order" style={{ padding: '76px 5%', background: C.charcoal, scrollMarginTop: 68 }}>
      <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 34, alignItems: 'start' }}>
        <div>
          <span className="mono" style={{ fontSize: 11, color: C.ember, letterSpacing: 2, textTransform: 'uppercase' }}>Order Briquettes</span>
          <h2 className="pf" style={{ fontSize: 38, marginTop: 8, marginBottom: 12 }}>Request Your Briquette Supply</h2>
          <p style={{ color: C.smoke, lineHeight: 1.75, marginBottom: 24 }}>
            Send your customer details, preferred briquette product or bag size, quantity, and delivery needs. The Maa Briquette team will confirm availability, pricing, delivery timing, and payment details.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a className="btn-green" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>WhatsApp Us</a>
            <a className="btn-s" href={PHONE_TEL} style={{ textDecoration: 'none' }}>Call Now</a>
          </div>
          <img src="/brand.jpg" alt="Maa Briquette products" style={{ width: 'min(100%, 430px)', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '50%', display: 'block', margin: '52px auto 0' }} />
        </div>

        <div className="card" style={{ padding: 30 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '18px 0' }}>
              <h3 className="pf" style={{ fontSize: 26, marginBottom: 10, color: C.success }}>Order Submitted</h3>
              <p style={{ color: C.smoke, marginBottom: 18 }}>Our team will contact you within 24 hours.</p>
              <div style={{ background: C.ash, borderRadius: 8, padding: '10px 18px', display: 'inline-block', marginBottom: 18 }}>
                <span style={{ fontSize: 12, color: C.smoke }}>Order ID: </span><span className="mono" style={{ color: C.ember, fontWeight: 700 }}>{oid}</span>
              </div><br />
              <button className="btn-s" onClick={() => setDone(false)}>Place Another Order</button>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              {[
                ['customerName', 'Customer Name', 'Your full name or business name'],
                ['phone', 'Phone / WhatsApp', '+254...'],
                ['email', 'Email', 'your@email.com'],
              ].map(([key, label, placeholder]) => (
                <Field key={key} label={label}>
                  <input className="inp" type={key === 'email' ? 'email' : 'text'} placeholder={placeholder} value={form[key]} onChange={upd(key)} />
                </Field>
              ))}
              <Field label="Location"><input className="inp" placeholder="Town, county, estate, or business location" value={form.location} onChange={upd('location')} /></Field>
              <Field label="Customer Type">
                <select className="inp" value={form.customerType} onChange={upd('customerType')}>
                  <option value="">Select customer type</option>
                  <option>Home / Household</option>
                  <option>Hotel / Restaurant</option>
                  <option>Business</option>
                  <option>Institution / School</option>
                  <option>Distributor / Bulk Buyer</option>
                </select>
              </Field>
              <Field label="Product / Bag Size">
                <select className="inp" value={form.productSize} onChange={upd('productSize')}>
                  <option value="">Select product or bag size</option>
                  {PRODUCTS.filter((p) => p.available).map((p) => <option key={p.name}>{p.name} - {p.sizes}</option>)}
                </select>
              </Field>
              <Field label="Quantity"><input className="inp" placeholder="e.g. 10 bags, 100 kg, or 500 kg" value={form.quantity} onChange={upd('quantity')} /></Field>
              <Field label="Delivery Option">
                <select className="inp" value={form.deliveryOption} onChange={upd('deliveryOption')}>
                  <option value="">Select delivery option</option>
                  <option>Delivery to my location</option>
                  <option>Pickup / collect from Maa Briquette</option>
                  <option>Discuss with sales team</option>
                </select>
              </Field>
              <Field label="Message / Notes"><textarea className="inp" rows={4} placeholder="Preferred delivery date, recurring supply needs, access details, or special notes..." value={form.notes} onChange={upd('notes')} style={{ resize: 'vertical' }} /></Field>
              {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <button className="btn-p" type="submit" style={{ width: '100%', padding: 13 }} disabled={!canSubmit}>Submit Briquette Order Request</button>
              <p style={{ fontSize: 12, color: C.smoke, textAlign: 'center', marginTop: 10 }}>No payment required at this stage.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [error, setError] = useState('');
  const trackContact = async (action) => {
    setError('');
    try {
      await submitToFormspree(FORMSPREE_CONTACT_ENDPOINT, action);
    } catch {
      setError('Could not send your request. Please use the contact link directly.');
    }
  };

  return (
    <section id="contact" style={{ padding: '76px 5%', background: C.coal, scrollMarginTop: 68 }}>
      <SectionHeading kicker="Contact" title="Talk To Maa Briquette" body="Reach out for briquette orders, hotel partnerships, waste collection, bulk supply, or delivery questions." />
      <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16, maxWidth: 1120, margin: '0 auto' }}>
        {[
          ['Email', 'maabriquette@gmail.com', 'mailto:maabriquette@gmail.com'],
          ['Phone', PHONE_DISPLAY, PHONE_TEL],
          ['WhatsApp', PHONE_DISPLAY, WHATSAPP_URL],
          ['Location / Service Area', 'Narok, Kenya', ''],
        ].map(([label, value, link]) => (
          <div key={label} className="card card-ember" style={{ padding: 20, height: '100%' }}>
            <div style={{ fontSize: 12, color: C.smoke, marginBottom: 8 }}>{label}</div>
            {link ? (
              <a href={link} target={link.startsWith('http') ? '_blank' : undefined} rel={link.startsWith('http') ? 'noopener noreferrer' : undefined} onClick={() => trackContact({ type: label, value, link })} style={{ color: C.ember, textDecoration: 'none', fontWeight: 700, wordBreak: 'break-word' }}>
                {value}
              </a>
            ) : (
              <div style={{ color: C.mist, fontWeight: 700 }}>{value}</div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 28 }}>
        <a className="btn-green" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>WhatsApp Us</a>
        <a className="btn-p" href={PHONE_TEL} style={{ textDecoration: 'none' }}>Call Now</a>
        <a className="btn-s" href="mailto:maabriquette@gmail.com" style={{ textDecoration: 'none' }}>Email Maa Briquette</a>
      </div>
      {error && <div style={{ color: C.danger, fontSize: 12, textAlign: 'center', marginTop: 18 }}>{error}</div>}
    </section>
  );
};

const MainLandingPage = () => (
  <>
    <HeroSection />
    <FirePerformanceSection />
    <AboutSection />
    <ProductsSection />
    <BenefitsSection />
    <OrderSection />
    <ContactSection />
  </>
);

const WasteCollectionPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [refCode] = useState('WCP-' + Math.random().toString(36).substr(2, 6).toUpperCase());
  const [form, setForm] = useState({
    business: '',
    contact: '',
    phone: '',
    email: '',
    location: '',
    wasteType: '',
    quantity: '',
    frequency: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const upd = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const canSubmit = form.business && form.contact && form.phone && form.email && form.location && form.wasteType && form.quantity && form.frequency && !isSubmitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);
      await submitToFormspree(FORMSPREE_WASTE_ENDPOINT, { ...form, refCode });
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ paddingTop: 70 }}>
      <section id="waste-home" style={{ padding: '96px 5% 70px', background: `radial-gradient(circle at center, rgba(74,124,89,.22), transparent 38%), ${C.coal}`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', alignItems: 'center', gap: 36 }}>
        <div style={{ maxWidth: 620 }}>
          <div className="fu" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <div style={{ height: 2, width: 36, background: C.leafL }} />
            <span className="mono" style={{ fontSize: 11, color: C.leafL, letterSpacing: 2, textTransform: 'uppercase' }}>Waste-to-Energy Program</span>
          </div>
          <h1 className="pf fu2" style={{ fontSize: 'clamp(34px,5vw,58px)', lineHeight: 1.12, marginBottom: 20 }}>
            <span style={{ display: 'block' }}>Your Hotel Waste</span>
            <span style={{ display: 'block', color: C.leafL, marginTop: 6 }}>Becomes Clean Fuel</span>
            <span style={{ display: 'block', marginTop: 6 }}>You Get Discounts</span>
          </h1>
          <p className="fu3" style={{ fontSize: 17, color: C.mist, lineHeight: 1.75, marginBottom: 30, maxWidth: 520 }}>
            Hotels, restaurants, farms, workshops, and businesses can partner with Maa Briquette to supply suitable biomass and dry organic waste. We review the material, arrange collection where practical, and use accepted waste to support cleaner briquette production and recycling.
          </p>
          <div className="fu4" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn-green" style={{ fontSize: 15, padding: '13px 28px' }} onClick={() => scrollToHash('#waste-form')}>Request Collection</button>
            <button className="btn-p" style={{ fontSize: 15, padding: '13px 28px' }} onClick={() => navigate('/', '#order')}>Order Briquettes</button>
            <a className="btn-s" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: 15 }}>WhatsApp Us</a>
          </div>
        </div>
        <div className="card waste-circle" style={{ width: 380, height: 380, padding: 42, borderRadius: '50%', borderColor: 'rgba(74,124,89,.28)', background: 'radial-gradient(circle at center, rgba(106,175,122,.16), rgba(13,13,13,.88) 62%)', boxShadow: '0 20px 60px rgba(74,124,89,.16)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', justifySelf: 'center', textAlign: 'center', overflow: 'hidden' }}>
          <h2 className="pf" style={{ fontSize: 26, lineHeight: 1.1, marginBottom: 14, color: C.leafL }}>Partnership<br />Promise</h2>
          <p style={{ color: C.mist, lineHeight: 1.55, fontSize: 13, maxWidth: 270 }}>Your business reduces waste pressure. Maa Briquette gains better raw material for recycling and briquette production. Partners may qualify for discounted briquette supply or other supply benefits after waste suitability and volume are confirmed.</p>
        </div>
      </section>

      <section id="how-it-works" style={{ padding: '76px 5%', background: C.coal, scrollMarginTop: 68 }}>
        <SectionHeading color={C.leafL} kicker="How It Works" title="Simple Collection, Clear Value" body="A practical pickup and discount flow for hotels and businesses." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 18, maxWidth: 960, margin: '0 auto' }}>
          {[
            ['01', 'Separate Usable Waste', 'Your business keeps usable biomass or dry organic waste separate from plastics, glass, metal, and wet contaminants.'],
            ['02', 'Schedule Collection', 'Maa Briquette reviews the material, confirms location and volume, then schedules pickup or another collection arrangement.'],
            ['03', 'Process and Recycle', 'Accepted waste is processed and recycled as part of Maa Briquette clean fuel production.'],
            ['04', 'Receive Supply Benefit', 'Partners may qualify for discounted briquette supply or a similar benefit based on confirmed waste contribution.'],
          ].map(([step, title, desc]) => (
            <div key={step} className="card" style={{ padding: 24, borderColor: 'rgba(74,124,89,.15)', background: 'rgba(74,124,89,.03)' }}>
              <div className="mono" style={{ fontSize: 11, color: C.leafL, marginBottom: 14 }}>STEP {step}</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{title}</h3>
              <p style={{ fontSize: 13, color: C.smoke, lineHeight: 1.7 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="partnership"
        style={{
          minHeight: 520,
          padding: '120px 5%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,.82) 0%, rgba(0,0,0,.72) 48%, rgba(0,0,0,.84) 100%), url("/mochu briquette.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          scrollMarginTop: 68,
        }}
      >
        <div style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center', textShadow: '0 2px 18px rgba(0,0,0,.72)' }}>
          <span className="mono" style={{ fontSize: 11, color: C.leafL, letterSpacing: 2, textTransform: 'uppercase', display: 'block', marginBottom: 14 }}>Hotel / Business Partnership</span>
          <h2 className="pf" style={{ fontSize: 'clamp(34px,5vw,58px)', lineHeight: 1.08, fontWeight: 900, marginBottom: 24 }}>Turn Waste Handling Into Fuel Savings</h2>
          <p style={{ color: C.mist, lineHeight: 1.8, fontSize: 16, margin: '0 auto 16px', maxWidth: 760 }}>Hotels and businesses often generate material that can support recycling and briquette production when it is sorted properly. Maa Briquette works with partners to review waste type, confirm whether it is usable, and create a practical collection plan.</p>
          <p style={{ color: C.mist, lineHeight: 1.8, fontSize: 15, margin: '0 auto', maxWidth: 720 }}>For qualified partners, the benefit can include discounted briquette supply, a cleaner operating environment, reduced disposal pressure, and lower long-term fuel costs through a circular supply relationship.</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 30, textShadow: 'none' }}>
            <button className="btn-green" onClick={() => scrollToHash('#waste-form')}>Request Collection</button>
            <button className="btn-p" onClick={() => navigate('/', '#order')}>Order Briquettes</button>
          </div>
        </div>
      </section>

      <section id="discount" style={{ padding: '70px 5%', background: C.coal, scrollMarginTop: 68 }}>
        <SectionHeading color={C.leafL} kicker="Partner Discount" title="Discounted Briquette Supply For Waste Partners" body="Businesses that provide confirmed usable waste may qualify for discounted briquette supply or a supply benefit. Final terms depend on waste type, volume, consistency, collection cost, and briquette order needs." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, maxWidth: 920, margin: '0 auto' }}>
          {[
            ['Volume Matters', 'Consistent usable waste makes it easier to plan collection and calculate a fair supply benefit.'],
            ['Quality Matters', 'Clean, separated material is more useful for recycling and briquette production than contaminated mixed waste.'],
            ['Supply Benefit', 'Qualified partners can discuss discounted briquette supply for hotel kitchens, institutions, or business fuel needs.'],
          ].map(([title, desc]) => (
            <div key={title} className="card" style={{ padding: 24, borderColor: 'rgba(74,124,89,.18)' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: C.leafL }}>{title}</h3>
              <p style={{ color: C.smoke, fontSize: 13, lineHeight: 1.7 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="waste-types" style={{ padding: '76px 5%', background: C.charcoal, scrollMarginTop: 68 }}>
        <SectionHeading color={C.leafL} kicker="Waste Types Accepted" title="Suitable Waste Streams For Review" body="Contact Maa Briquette to confirm whether your material is acceptable before storing or transporting it. Final acceptance depends on moisture, contamination, volume, and pickup location." />
        <div className="waste-types-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18, maxWidth: 920, margin: '0 auto' }}>
          {[
            ['Biomass waste', 'Plant-based material that can be reviewed for recycling or briquette production use.'],
            ['Agricultural waste', 'Maize cobs, stalks, husks, and other dry crop residues where suitable.'],
            ['Sawdust / wood waste', 'Clean sawdust or untreated wood residues may be considered where applicable.'],
            ['Organic dry waste', 'Dry organic material from kitchens, farms, hotels, and businesses can be reviewed if separated properly.'],
          ].map(([title, desc]) => (
            <div key={title} className="card" style={{ padding: 22, borderColor: 'rgba(74,124,89,.18)' }}>
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>{title}</h3>
              <p style={{ color: C.smoke, fontSize: 13, lineHeight: 1.7 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="waste-form" style={{ padding: '76px 5%', background: C.coal, scrollMarginTop: 68 }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <span className="mono" style={{ fontSize: 11, color: C.leafL, letterSpacing: 2, textTransform: 'uppercase' }}>Request Waste Collection / Partnership</span>
          <h2 className="pf" style={{ fontSize: 34, marginTop: 8, marginBottom: 10 }}>Request Waste Collection or Partnership Review</h2>
          <p style={{ color: C.smoke, lineHeight: 1.7, marginBottom: 28 }}>Share your waste type, estimated quantity, and pickup needs. Maa Briquette will contact you to confirm suitability, collection options, and any partner supply benefit.</p>
          <div className="card" style={{ padding: 30, borderColor: 'rgba(74,124,89,.2)' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '18px 0' }}>
                <h3 className="pf" style={{ fontSize: 26, marginBottom: 10, color: C.leafL }}>Request Submitted</h3>
                <p style={{ color: C.smoke, marginBottom: 18, lineHeight: 1.7 }}>Our team will contact you within 48 hours.</p>
                <div style={{ background: C.ash, borderRadius: 8, padding: '12px 18px', marginBottom: 20, display: 'inline-block' }}>
                  <span style={{ fontSize: 13, color: C.mist }}>Reference: </span><span className="mono" style={{ color: C.leafL }}>{refCode}</span>
                </div><br />
                <button className="btn-green" onClick={() => setSubmitted(false)}>Submit Another Request</button>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 16px' }}>
                  <div style={{ gridColumn: '1/-1' }}><Field label="Business / Hotel Name"><input className="inp" value={form.business} onChange={upd('business')} placeholder="e.g. Mara Hotel" /></Field></div>
                  <Field label="Contact Person"><input className="inp" value={form.contact} onChange={upd('contact')} placeholder="Full name" /></Field>
                  <Field label="Phone / WhatsApp"><input className="inp" value={form.phone} onChange={upd('phone')} placeholder="+254..." /></Field>
                  <Field label="Email"><input className="inp" type="email" value={form.email} onChange={upd('email')} placeholder="you@business.co.ke" /></Field>
                  <Field label="Location"><input className="inp" value={form.location} onChange={upd('location')} placeholder="Town, county, area" /></Field>
                  <Field label="Waste Type">
                    <select className="inp" value={form.wasteType} onChange={upd('wasteType')}>
                      <option value="">Select waste type</option>
                      <option>Kitchen food waste</option>
                      <option>Agricultural waste</option>
                      <option>Restaurant organic waste</option>
                      <option>Mixed separated biomass</option>
                      <option>Other organic waste</option>
                    </select>
                  </Field>
                  <Field label="Estimated Waste Quantity" hint="Use kg per week or kg per month if known.">
                    <input className="inp" value={form.quantity} onChange={upd('quantity')} placeholder="e.g. 300 kg/month" />
                  </Field>
                  <div style={{ gridColumn: '1/-1' }}>
                    <Field label="Pickup Frequency">
                      <select className="inp" value={form.frequency} onChange={upd('frequency')}>
                        <option value="">Select pickup frequency</option>
                        <option>Daily</option>
                        <option>Twice a week</option>
                        <option>Weekly</option>
                        <option>Bi-weekly</option>
                        <option>Monthly</option>
                      </select>
                    </Field>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <Field label="Message / Notes"><textarea className="inp" rows={4} value={form.notes} onChange={upd('notes')} placeholder="Access details, waste handling notes, preferred pickup time..." style={{ resize: 'vertical' }} /></Field>
                  </div>
                </div>
                <div style={{ background: 'rgba(74,124,89,.08)', border: '1px solid rgba(74,124,89,.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
                  <div style={{ fontSize: 13, color: C.leafL, fontWeight: 600, marginBottom: 3 }}>Partner Benefit Review</div>
                  <div style={{ fontSize: 13, color: C.mist }}>Maa Briquette will review your waste type, quantity, and pickup location before confirming any briquette supply discount or partner benefit.</div>
                </div>
                {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{error}</div>}
                <button className="btn-green" type="submit" style={{ width: '100%', padding: 14, fontSize: 15 }} disabled={!canSubmit}>Submit Waste Collection Request</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section id="contact-cta" style={{ padding: '70px 5%', textAlign: 'center', background: `linear-gradient(135deg,${C.coal},rgba(74,124,89,.08))` }}>
        <h2 className="pf" style={{ fontSize: 34, marginBottom: 12 }}>Ready to Discuss Collection?</h2>
        <p style={{ color: C.smoke, marginBottom: 24 }}>Talk to Maa Briquette about your waste type, pickup location, partner benefits, and briquette supply needs.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-green" onClick={() => scrollToHash('#waste-form')}>Request Collection</button>
          <a className="btn-p" href={PHONE_TEL} style={{ textDecoration: 'none' }}>Call Now</a>
          <a className="btn-s" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>WhatsApp Us</a>
          <button className="btn-s" onClick={() => navigate('/', '#home')}>Back to Main Page</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 18, flexWrap: 'wrap', marginTop: 26, color: C.smoke, fontSize: 13 }}>
          <a href="mailto:maabriquette@gmail.com" style={{ color: C.leafL, textDecoration: 'none' }}>maabriquette@gmail.com</a>
          <a href={PHONE_TEL} style={{ color: C.leafL, textDecoration: 'none' }}>{PHONE_DISPLAY}</a>
          <span>Total, Narok</span>
        </div>
      </section>
    </div>
  );
};

const FloatingWhatsApp = () => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="WhatsApp Maa Briquette"
    className="floating-whatsapp"
    style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 120, width: 56, height: 56, borderRadius: '50%', background: C.leaf, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontWeight: 900, boxShadow: '0 10px 30px rgba(0,0,0,.35)', border: '1px solid rgba(255,255,255,.16)' }}
  >
    <svg aria-hidden="true" viewBox="0 0 32 32" width="30" height="30" fill="currentColor" focusable="false">
      <path d="M16.02 3.2A12.72 12.72 0 0 0 5.1 22.45L3.4 28.8l6.5-1.7a12.66 12.66 0 0 0 6.1 1.55h.01A12.73 12.73 0 0 0 16.02 3.2Zm0 23.28h-.01a10.54 10.54 0 0 1-5.37-1.47l-.38-.22-3.85 1.01 1.03-3.75-.25-.39a10.55 10.55 0 1 1 8.83 4.82Zm5.78-7.9c-.32-.16-1.88-.93-2.17-1.03-.29-.11-.5-.16-.71.16-.21.32-.82 1.03-1 1.24-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.57-.94-.84-1.58-1.88-1.76-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.71-1.71-.98-2.34-.26-.62-.52-.53-.71-.54h-.61c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.38 4.75.75.32 1.34.52 1.8.66.76.24 1.44.2 1.99.12.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.38.18-1.51-.08-.13-.29-.21-.61-.37Z" />
    </svg>
  </a>
);

const Footer = () => (
  <footer style={{ background: C.charcoal, borderTop: `1px solid ${C.ash}`, padding: '36px 5% 92px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
          <img src="/maa logo.png" alt="Maa Briquette" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
        </div>
        <span className="pf" style={{ fontWeight: 700 }}>Maa Briquette</span>
      </div>
      <p style={{ fontSize: 12, color: C.smoke }}>Copyright 2026 Maa Briquette Ltd - Narok, Kenya - <span style={{ color: C.leafL }}>Circular Economy</span></p>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: C.smoke }}>maabriquette@gmail.com</span>
        <span style={{ fontSize: 12, color: C.smoke }}>{PHONE_DISPLAY}</span>
      </div>
    </div>
  </footer>
);

export default function App() {
  const [path, setPath] = useState(window.location.pathname === '/waste' ? '/waste' : '/');

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `${CSS}
html{scroll-behavior:smooth}
.btn-g{display:inline-flex;align-items:center;min-height:34px}
.site-nav .nav-links{scrollbar-width:none}
.site-nav .nav-links::-webkit-scrollbar{display:none}
.hamburger{display:none}
.hamburger span{display:block;width:24px;height:2px;background:${C.cream};border-radius:999px;margin:5px 0;transition:transform .22s ease, opacity .22s ease}
.hamburger.open span:nth-child(1){transform:translateY(7px) rotate(45deg)}
.hamburger.open span:nth-child(2){opacity:0}
.hamburger.open span:nth-child(3){transform:translateY(-7px) rotate(-45deg)}
.mobile-menu{display:none}
@media (max-width: 1024px) and (min-width: 769px) {
  .benefits-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  .contact-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}
@media (max-width: 768px) {
  .site-nav {
    min-height: 76px !important;
    height: auto !important;
    padding: 8px 3% !important;
    align-items: center !important;
    overflow: visible !important;
  }
  .site-nav .nav-brand span {
    display: none !important;
  }
  .site-nav .nav-links {
    display: none !important;
  }
  .hamburger {
    display: block !important;
    margin-left: auto !important;
    min-height: 44px !important;
  }
  .mobile-menu {
    position: absolute !important;
    top: calc(100% + 8px) !important;
    left: 3% !important;
    right: 3% !important;
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 4px !important;
    padding: 10px !important;
    background: rgba(13,13,13,.98) !important;
    border: 1px solid ${C.ash} !important;
    border-radius: 8px !important;
    box-shadow: 0 18px 42px rgba(0,0,0,.38) !important;
    max-height: 0 !important;
    opacity: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
    transform: translateY(-8px) !important;
    transition: max-height .26s ease, opacity .2s ease, transform .22s ease !important;
  }
  .mobile-menu.open {
    max-height: 420px !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    transform: translateY(0) !important;
  }
  .mobile-menu a {
    display: block !important;
    padding: 12px 14px !important;
    border-radius: 6px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
  }
  .mobile-menu a:hover {
    background: ${C.ash} !important;
  }
  .products-grid {
    grid-template-columns: 1fr !important;
  }
  .benefits-grid {
    grid-template-columns: 1fr !important;
  }
  .contact-grid {
    grid-template-columns: 1fr !important;
  }
  .about-highlight {
    grid-template-columns: 1fr !important;
    gap: 6px !important;
  }
  .fire-overlay {
    left: 14px !important;
    right: 14px !important;
    bottom: 14px !important;
    max-width: none !important;
    padding: 14px !important;
  }
  .fire-image {
    height: 220px !important;
  }
  #home {
    padding-top: 136px !important;
  }
  #waste-home {
    padding-top: 116px !important;
  }
  .waste-circle {
    width: min(88vw, 330px) !important;
    height: min(88vw, 330px) !important;
    padding: 34px !important;
    margin: 0 auto !important;
  }
  .waste-types-grid {
    grid-template-columns: 1fr !important;
  }
  .floating-whatsapp {
    right: 14px !important;
    bottom: 14px !important;
    width: 52px !important;
    height: 52px !important;
  }
}`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const syncPath = () => {
      setPath(window.location.pathname === '/waste' ? '/waste' : '/');
      window.setTimeout(() => scrollToHash(window.location.hash), 80);
    };
    window.addEventListener('popstate', syncPath);
    syncPath();
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.coal }}>
      <Navbar path={path} />
      <main style={{ flex: 1 }}>
        {path === '/waste' ? <WasteCollectionPage /> : <MainLandingPage />}
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
